import dayjs from "dayjs"
import { includes } from "lodash-es"
import { isProdVercelEnv } from "@aix/env"

import { SubscriptionNameMap, ESubscriptionTier } from "../constants/enums"
import { HARDCODED_MONTHLY_PRO } from "../constants/enums"

import type { ITeam, ITeamSubscriptionMeta } from "../constants/prismaTypings"
import { asCents, asEpochSeconds } from "../types/branded"

export const STRIPE_PRODUCT_IDS = (
  isProdVercelEnv
    ? {
        prod_OBtNdSGqPifAkk: ESubscriptionTier.Free,
        prod_R2IchNOIPunMU8: ESubscriptionTier.Starter,
        prod_R2Id1Q86EVC1nE: ESubscriptionTier.Creator,
        prod_R2Id3GtzxxoV0r: ESubscriptionTier.Pro,
        prod_RzBCSWPJCgcEKX: ESubscriptionTier.TopUp,
        // Ads Platform Products (legacy)
        prod_TFWSqOIOFAK24O: ESubscriptionTier.StarterPlan,
        prod_TFWTzTKsAYJBwi: ESubscriptionTier.BusinessPlan,
        prod_TFWUm4e64s3341: ESubscriptionTier.AgencyPlan,
        // UGC live-mode products (VidMax Starter/Growth/Pro, created 2026-04-24)
        prod_UOeeV3oybRLt3V: ESubscriptionTier.StarterPlan,
        prod_UOeeTM6FMMUyJf: ESubscriptionTier.BusinessPlan,
        prod_UOeednvuRDcK3i: ESubscriptionTier.AgencyPlan,
        // "Hobby": "Hobby",
        // "prod_OBtOEtdKgiFRtz": "Pro",
        // "prod_OoyWpfq6n1N1jL": "Agency",
        // "prod_OBtOK02JyP18P0": "Enterprise",
      }
    : {
        prod_OBtZR1BopprX0I: ESubscriptionTier.Free,
        prod_R2IdJGwAWowlan: ESubscriptionTier.Starter,
        prod_R2Id7YTdzepa77: ESubscriptionTier.Creator,
        prod_R2IdkxdzmMVnYc: ESubscriptionTier.Pro,
        prod_RzBDG3PvFN3dAU: ESubscriptionTier.TopUp,
        // Ads Platform Products (legacy - empty metadata, kept for migration safety)
        prod_TFWYy0IC71FNqO: ESubscriptionTier.StarterPlan,
        prod_TFWY7WFatPl31Y: ESubscriptionTier.BusinessPlan,
        prod_TFWZJ47IRXN5Ku: ESubscriptionTier.AgencyPlan,
        // UGC test-mode products (VidMax Starter/Growth/Pro, created 2026-04-22)
        prod_UNvufgaWFTqoAP: ESubscriptionTier.StarterPlan,
        prod_UNvu9qASSykJkK: ESubscriptionTier.BusinessPlan,
        prod_UNvuRNI2x2RdnB: ESubscriptionTier.AgencyPlan,
        // "Hobby": "Hobby",
        // "prod_OoEpdXNj0DaXI5": "Agency",
        // "Enterprise": "Enterprise", // None on staging
      }
) as Record<string, ESubscriptionTier>

// Don't really know why we're doing this but okay
const SUBSCRIPTION_NAME_TO_TIER_MAP = {
  Free: ESubscriptionTier.Free,
  Starter: ESubscriptionTier.Starter,
  Creator: ESubscriptionTier.Creator,
  Pro: ESubscriptionTier.Pro,
  "Starter Plan": ESubscriptionTier.StarterPlan,
  "Business Plan": ESubscriptionTier.BusinessPlan,
  "Agency Plan": ESubscriptionTier.AgencyPlan,
  // UGC ads platform products (named on Stripe as "VidMax Starter/Growth/Pro")
  "VidMax Starter": ESubscriptionTier.StarterPlan,
  "VidMax Growth": ESubscriptionTier.BusinessPlan,
  "VidMax Pro": ESubscriptionTier.AgencyPlan,
} as const

// Server-only Stripe SDK usage — import from stripe.server.ts instead
// getStripeInstance() has been moved to stripe.server.ts to avoid
// pulling the 165 KB Stripe SDK into the browser bundle.

const DEFAULT_SUBSCRIPTION_STATE = {
  subscriptionType: ESubscriptionTier.Free,
  subscriptionName: SubscriptionNameMap[ESubscriptionTier.Free],
  isActive: false,
  isTrial: false,
  isPastDue: false,
  hasFreePosts: false,
  subscriptionMeta: {
    subscriptionId: "",
    subscriptionStatus: "canceled" as const,
    priceId: "",
    interval: "month" as const,
    productId: "",
    cancelAtPeriodEnd: false,
    canceledAt: asEpochSeconds(0),
    latestInvoiceId: "",
    transactionValue: asCents(0),
    currencyCode: "usd",
    customerId: "",
    startDate: asEpochSeconds(0),
    billingCycleAnchor: asEpochSeconds(0),
    createdAt: asEpochSeconds(0),
  },
  cancelAtPeriodEnd: false,
  trialDaysLeft: 0,
  subPausedUntil: undefined,
  hasTrialOnSignup: false,
}

export const calcSubscriptionState = (
  team?: ITeam,
): {
  subscriptionType: ESubscriptionTier
  subscriptionName: string
  isActive: boolean
  isTrial: boolean
  isPastDue: boolean
  hasFreePosts: boolean
  subscriptionMeta: ITeamSubscriptionMeta
  cancelAtPeriodEnd: boolean
  trialDaysLeft: number
  subPausedUntil?: string
  hasTrialOnSignup: boolean
} => {
  if (!team) {
    return DEFAULT_SUBSCRIPTION_STATE
  }
  const isTrial =
    includes(["trialing"], team?.stripeSubscription?.subscriptionStatus) && !team?.stripeSubscription?.cancelAtPeriodEnd
  const isActive = includes(["active", "trialing"], team?.stripeSubscription?.subscriptionStatus)
  const isPastDue = includes(["past_due", "incomplete_expired"], team?.stripeSubscription?.subscriptionStatus)

  let state = ESubscriptionTier.Free

  if (
    team.stripeSubscription &&
    (team?.stripeSubscription?.priceId === HARDCODED_MONTHLY_PRO ||
      isTrial ||
      team?.stripeSubscription?.subscriptionStatus === "active")
  ) {
    const { productId } = team.stripeSubscription
    // Takes the product type but falls back to the free plan
    state = STRIPE_PRODUCT_IDS[productId] ?? ESubscriptionTier.Free
  }

  const hasFreePosts = state === ESubscriptionTier.Free && Number(team?.usageMisc?.creditsLeft || 3) > 0

  const result = {
    subscriptionType: state,
    subscriptionName: SubscriptionNameMap[state],
    isActive,
    subscriptionMeta: {
      ...((team?.stripeSubscription || {}) as ITeamSubscriptionMeta),
    },
    hasFreePosts,
    isTrial,
    hasTrialOnSignup: false,
    // hasTrialOnSignup: isEmpty(team?.stripeSubscription), // Give them a trial if they have no previous subscription
    isPastDue, // Should be used to show a "update payment method" button
    cancelAtPeriodEnd: team?.stripeSubscription?.cancelAtPeriodEnd || false,
    trialDaysLeft: dayjs.unix(team?.stripeSubscription?.trialEnd || 0).diff(dayjs(), "day"),
    subPausedUntil: dayjs.unix(team?.stripeSubscription?.pauseResumesAt || 0).isAfter(dayjs())
      ? dayjs.unix(team?.stripeSubscription?.pauseResumesAt || 0).format("MM/DD/YY")
      : undefined,
  }

  return result
}

/**
 * Converts Stripe subscription name or product ID to subscription tier
 * Uses proper case matching for ESubscriptionTier enum values
 * @throws Error when subscription name is invalid
 */
const convertStripeSubNameOrProductIdToTier = (subscriptionNameOrProductId?: string): ESubscriptionTier => {
  const subscriptionName = STRIPE_PRODUCT_IDS[subscriptionNameOrProductId || ""] ?? subscriptionNameOrProductId

  const evaluatedTier = SUBSCRIPTION_NAME_TO_TIER_MAP[subscriptionName || ""]
  if (!evaluatedTier) {
    throw new Error(`convertStripeSubNameOrProductIdToTier: Invalid subscription name: ${subscriptionName}`)
  }

  return evaluatedTier
}
